import { useEffect, useRef, useState } from 'react';
import { zh } from '../i18n';

/**
 * Auto-growing composer (SPEC §10). ⌘/Ctrl+Enter sends; the send button becomes
 * 停止 while a turn runs; and a disabled composer always carries the REASON
 * inline — 憑證未設定 / 啟動中 / 已離線 / 額度已滿 — because "greyed out with no
 * explanation" is the failure mode that generates support tickets.
 *
 * Two kinds of reason, deliberately separated:
 *  - `disabledReason` is a STATE the client can observe (offline, maintenance,
 *    no credential, runner starting). It disables the field, and it clears
 *    itself when the state changes.
 *  - `noticeText` is the server's answer to the last attempt (429 daily budget,
 *    409 turn in progress). It is displayed but must NOT disable the field: a
 *    reason that only the server can retract would otherwise leave the user
 *    with no way to try again short of reloading the page.
 */

export interface ComposerProps {
  readonly onSend: (text: string) => void;
  readonly onStop: () => void;
  readonly running: boolean;
  readonly disabledReason: string | null;
  readonly noticeText: string | null;
  readonly maxChars: number;
  readonly busy?: boolean;
}

export function Composer({
  onSend,
  onStop,
  running,
  disabledReason,
  noticeText,
  maxChars,
  busy = false,
}: ComposerProps): JSX.Element {
  const [text, setText] = useState('');
  const areaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    area.style.height = 'auto';
    area.style.height = `${Math.min(area.scrollHeight, window.innerHeight * 0.4)}px`;
  }, [text]);

  const tooLong = text.length > maxChars;
  const canSend = text.trim().length > 0 && !tooLong && disabledReason === null && !busy;

  const send = (): void => {
    if (!canSend) return;
    onSend(text.trim());
    setText('');
  };

  return (
    <div className="composer">
      <div className="composer__row">
        <textarea
          ref={areaRef}
          className="composer__input"
          rows={1}
          value={text}
          placeholder={zh.chat.composerPlaceholder}
          aria-label={zh.chat.composerPlaceholder}
          disabled={disabledReason !== null}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              send();
            }
          }}
        />
        {running ? (
          <button type="button" className="composer__send" onClick={onStop}>
            {busy ? zh.chat.stopping : zh.chat.stop}
          </button>
        ) : (
          <button type="button" className="composer__send" disabled={!canSend} onClick={send}>
            {zh.chat.send}
          </button>
        )}
      </div>
      {disabledReason !== null ? (
        <div className="composer__reason" role="status">
          {disabledReason}
        </div>
      ) : null}
      {disabledReason === null && noticeText !== null ? (
        <div className="composer__reason" role="status">
          {noticeText}
        </div>
      ) : null}
      {tooLong ? <div className="composer__reason">{zh.chat.tooLong(maxChars)}</div> : null}
    </div>
  );
}
